import { useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from '@tanstack/react-router';
import { shouldPromptTour, getTourById, type TourWithProgress } from '@pantry/api-client';
import type { TourTriggerGuard } from '@pantry/shared';
import type { PostHog } from 'posthog-js';

const DEVICE_TYPE = 'web' as const;

interface UseTourTriggersOptions {
  tours: TourWithProgress[] | undefined;
  isLoading: boolean;
  isActive: boolean;
  itemCount: number;
  posthog: PostHog | undefined;
}

/**
 * Custom hook that encapsulates tour prompt logic for immediate and on_route triggers.
 * Manages which tour (if any) should be prompted to the user.
 */
export function useTourTriggers({
  tours,
  isLoading,
  isActive,
  itemCount,
  posthog,
}: UseTourTriggersOptions) {
  const location = useLocation();
  const [tourToPrompt, setTourToPrompt] = useState<string | null>(null);
  const hasPromptedRef = useRef(false);
  const promptedOnRouteRef = useRef<Set<string>>(new Set());

  /** Evaluate trigger guards against current context. */
  const evaluateTriggerGuard = useCallback(
    (guard: TourTriggerGuard | undefined, allTours: TourWithProgress[]): boolean => {
      if (!guard) return true;
      if (guard.minItems !== undefined && itemCount < guard.minItems) return false;
      if (guard.hasCompletedTour) {
        const requiredTour = allTours.find((t) => t.tourId === guard.hasCompletedTour);
        if (!requiredTour || requiredTour.web.status !== 'completed') return false;
      }
      // minDaysActive is not evaluated client-side (would need account creation date)
      return true;
    },
    [itemCount]
  );

  // Check for immediate tours to prompt on initial load (only once per session)
  useEffect(() => {
    if (hasPromptedRef.current) return;
    if (!tours || isLoading || isActive || tourToPrompt) return;

    for (const tour of tours) {
      const progress = tour.web;
      if (shouldPromptTour(progress)) {
        const definition = getTourById(tour.tourId);
        if (definition?.triggerCondition === 'immediate') {
          if (!evaluateTriggerGuard(definition.triggerGuard, tours)) continue;
          hasPromptedRef.current = true;
          setTourToPrompt(tour.tourId);
          posthog?.capture('tour_prompted', {
            tour_id: tour.tourId,
            device_type: DEVICE_TYPE,
            has_completed_other_platform: tour.mobile?.status === 'completed',
          });
          break;
        }
      }
    }
  }, [tours, isLoading, isActive, tourToPrompt, posthog, evaluateTriggerGuard]);

  // Check for on_route tours whenever the route changes
  useEffect(() => {
    if (!tours || isLoading || isActive || tourToPrompt) return;

    const currentPath = location.pathname;

    for (const tour of tours) {
      const progress = tour.web;
      if (!shouldPromptTour(progress)) continue;

      const definition = getTourById(tour.tourId);
      if (definition?.triggerCondition !== 'on_route') continue;
      if (!definition.triggerRoute || currentPath !== definition.triggerRoute) continue;

      // Evaluate trigger guards
      if (!evaluateTriggerGuard(definition.triggerGuard, tours)) continue;

      // Only prompt each on_route tour once per session per route
      const key = `${tour.tourId}:${currentPath}`;
      if (promptedOnRouteRef.current.has(key)) continue;

      promptedOnRouteRef.current.add(key);
      setTourToPrompt(tour.tourId);
      posthog?.capture('tour_prompted', {
        tour_id: tour.tourId,
        device_type: DEVICE_TYPE,
        trigger: 'on_route',
        route: currentPath,
      });
      break;
    }
  }, [tours, isLoading, isActive, tourToPrompt, location.pathname, posthog, evaluateTriggerGuard]);

  const dismissPrompt = useCallback(() => {
    setTourToPrompt(null);
  }, []);

  return {
    tourToPrompt,
    setTourToPrompt,
    evaluateTriggerGuard,
    dismissPrompt,
  };
}
